import { mediaQueryLarge, requestIdleCallback, startViewTransition } from '@theme/utilities';
import PaginatedList from '@theme/paginated-list';

/**
 * A custom element that renders a pagniated results list
 */
export default class ResultsList extends PaginatedList {
  #bannerGrid;
  #bannerResizeObserver;
  #bannerLayoutFrame;

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    document.addEventListener('shopify:section:load', this.#handleSearchLayoutReset);
    document.addEventListener('view-transition-end', this.#handleSearchLayoutReset);
    document.addEventListener('filter:update', this.#handleFilterResultsUpdate);
    this.#resetGridViewOnSearchPage();
    this.#observeGridBanners();
    this.setAttribute('initialized', '');
  }

  disconnectedCallback() {
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
    document.removeEventListener('shopify:section:load', this.#handleSearchLayoutReset);
    document.removeEventListener('view-transition-end', this.#handleSearchLayoutReset);
    document.removeEventListener('filter:update', this.#handleFilterResultsUpdate);
    this.#bannerResizeObserver?.disconnect();
    cancelAnimationFrame(this.#bannerLayoutFrame);
  }

  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    if (this.#isSearchPage()) {
      this.#setLayout('default');
      return;
    }

    this.#animateLayoutChange(target.value);
  }

  /**
   * Sets the layout.
   *
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;

    if (!grid) return;

    await startViewTransition(() => this.#setLayout(value), ['product-grid']);

    requestIdleCallback(() => {
      const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
      sessionStorage.setItem(`product-grid-view-${viewport}`, value);
    });
  };

  /**
   * Animates the layout change.
   *
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;
    grid.setAttribute('product-grid-view', value);
    this.#scheduleGridBannerLayout();
  }

  #resetGridViewOnSearchPage() {
    if (!this.#isSearchPage()) return;

    const targetElement = mediaQueryLarge.matches
      ? this.querySelector('[data-grid-layout="desktop-default-option"]')
      : this.querySelector('[data-grid-layout="mobile-option"]');

    if (targetElement instanceof HTMLInputElement) {
      targetElement.checked = true;
    }

    this.#setLayout('default');
  }

  #isSearchPage() {
    const currentPath = window.location.pathname.replace(/\/$/, '') || '/';
    const searchPath =
      typeof Theme !== 'undefined' && Theme.routes?.search_url
        ? new URL(Theme.routes.search_url, window.location.origin).pathname.replace(/\/$/, '') || '/'
        : '/search';

    return currentPath === searchPath;
  }

  /**
   * Handles the media query change event.
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    this.#scheduleGridBannerLayout();

    const targetElement = event.matches
      ? this.querySelector('[data-grid-layout="desktop-default-option"]')
      : this.querySelector('[data-grid-layout="mobile-option"]');

    if (!(targetElement instanceof HTMLInputElement)) return;

    targetElement.checked = true;
    this.#setLayout('default');
  };

  #handleSearchLayoutReset = (event) => {
    const eventSectionId = event?.detail?.sectionId;
    if (eventSectionId && eventSectionId !== this.sectionId) return;

    this.#observeGridBanners();

    if (this.#isSearchPage()) {
      this.#resetGridViewOnSearchPage();
    }
  };

  #handleFilterResultsUpdate = () => {
    this.#bannerResizeObserver?.disconnect();
    this.#bannerGrid = null;
    cancelAnimationFrame(this.#bannerLayoutFrame);
  };

  #observeGridBanners() {
    const { grid } = this.refs;

    if (!(grid instanceof HTMLElement)) {
      this.#bannerResizeObserver?.disconnect();
      this.#bannerGrid = null;

      for (const banner of this.querySelectorAll('[data-banner-type]')) {
        if (banner instanceof HTMLElement) {
          banner.hidden = true;
        }
      }

      return;
    }

    if (this.#bannerGrid !== grid) {
      this.#bannerResizeObserver?.disconnect();
      this.#bannerGrid = grid;
      this.#bannerResizeObserver = new ResizeObserver(this.#scheduleGridBannerLayout);
      this.#bannerResizeObserver.observe(grid);
    }

    this.#scheduleGridBannerLayout();
  }

  #scheduleGridBannerLayout = () => {
    cancelAnimationFrame(this.#bannerLayoutFrame);
    this.#bannerLayoutFrame = requestAnimationFrame(this.#updateGridBannerLayout);
  };

  #countVisibleGridProducts(grid) {
    return grid.querySelectorAll('.product-grid__item--product:not([hidden])').length;
  }

  #updateGridBannerLayout = () => {
    const grid = this.#bannerGrid;
    if (!(grid instanceof HTMLElement)) return;

    const templateColumns = getComputedStyle(grid).gridTemplateColumns;
    const columnCount = templateColumns === 'none' ? 1 : templateColumns.split(/\s+/).filter(Boolean).length;
    const columns = Math.max(columnCount, 1);

    const device = mediaQueryLarge.matches ? 'desktop' : 'mobile';
    const productCount = this.#countVisibleGridProducts(grid);
    const productRowCount = productCount > 0 ? Math.ceil(productCount / columns) : 0;

    for (const banner of grid.querySelectorAll('[data-banner-type]')) {
      if (!(banner instanceof HTMLElement)) continue;

      const isCardBanner = banner.dataset.bannerType === 'card';
      const positionKey = isCardBanner ? `${device}Position` : `${device}Row`;
      const position = Number.parseInt(banner.dataset[positionKey] || '', 10);

      if (!Number.isInteger(position) || position < 1) {
        banner.hidden = true;
        continue;
      }

      const hasAnchor = isCardBanner
        ? productCount >= position
        : productRowCount >= position;

      if (!hasAnchor) {
        banner.hidden = true;
        continue;
      }

      banner.hidden = false;

      const productPosition = isCardBanner ? position : position * columns;
      banner.style.setProperty('--banner-order', String(productPosition * 2 + 1));
    }
  };
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}
